import {default as throttle} from "lodash/throttle"
import {trackEventSP} from 'js/studios/shared/tracking'

export class Header {
  static selector = "header[data-component=header]"

  constructor(el) {
    this.root = el
    this.transparentHeader = this.root.classList.contains("header--transparent")
    this.mobileMenuToggle = this.root.querySelector("button[data-action=toggle-mobile-menu]")
    this.headerMenu = this.root.querySelector(".header__menu")
    this.mobileNav = this.headerMenu.querySelector(".header__submenu[data-submenu=mobile-nav]")
    this.mobileToggleIcon = this.mobileMenuToggle.querySelector(".icon")
    this.menuBackdrop = this.root.querySelector("[data-action=close-menu]")
    this._menuOpen = false
    this.setupHandlers()
  }

  // for transparent headers (homepage), use scroll events to determine when to transition to normal header
  onScroll = _ev => {
    if (!this.transparentHeader) { return }
    // if a sub-menu is open, header is not transparent and scroll should change nothing
    if (this.menuOpen) { return }

    const
      scrollY = window.scrollY,
      heroSection = this.root.nextElementSibling,
      headerHeight = this.root.querySelector(".header__main").getBoundingClientRect().height,
      heroHeight = heroSection.getBoundingClientRect().height,
      startAlpha = headerHeight,
      endAlpha = Math.max(
        heroHeight - (1.5 * this.root.querySelector(".header__main").getBoundingClientRect().height),
        startAlpha,
      )

    if (scrollY < startAlpha) { // if not scrolled past start point, fully transparent
      this.root.style.setProperty("--header-alpha", 0)
      this.root.classList.remove("header--transparent-scrolled")
    } else if (scrollY < endAlpha) { // if scrolled a little but still over hero, partial transparency
      this.root.classList.add("header--transparent-scrolled")
      const alpha = Math.max((scrollY - startAlpha) / (endAlpha - startAlpha), .4)
      this.root.style.setProperty("--header-alpha", alpha)
    } else { // once near end of hero, fully solid header
      this.root.classList.add("header--transparent-scrolled")
      this.root.style.setProperty("--header-alpha", 1)
    }
  }

  get menuOpen() {
    return this._menuOpen
  }

  set menuOpen(val) {
    this._menuOpen = val

    if (this.transparentHeader) {
      if (val) {
        this.root.classList.add("header--transparent-scrolled")
        this.root.style.setProperty("--header-alpha", 1)
      } else {
        this.onScroll()
      }
    }
  }

  closeAllSubmenus = () => {
    for (const a of this.root.querySelectorAll(".header__main .header__nav__a--submenu-open")) {
      a.classList.remove("header__nav__a--submenu-open")
    }
    for (const submenu of this.root.querySelectorAll(".header__submenu--open")) {
      submenu.classList.remove("header__submenu--open")
    }
    this.mobileToggleIcon.classList.remove("icon--x")
    this.mobileToggleIcon.classList.add("icon--menu")
    this.menuOpen = false
  }

  openSubmenu = (submenuEl) => {
    this.closeAllSubmenus()
    submenuEl.classList.add("header__submenu--open")
    const anchor = this.root.querySelector(`.header__main nav a[data-action=submenu][data-submenu=${submenuEl.dataset.submenu}]`)
    if (anchor) {
      anchor.classList.add("header__nav__a--submenu-open")
    }
    this.mobileToggleIcon.classList.remove("icon--menu")
    this.mobileToggleIcon.classList.add("icon--x")
    this.menuOpen = true
  }

  toggleMobileMenu = _ev => {
    const isOpen = this.headerMenu.querySelector(".header__submenu--open")
    if (!isOpen) {
      this.openSubmenu(this.mobileNav)
    } else {
      this.closeAllSubmenus()
    }
    trackEventSP("click_nav_item", "browse", {label: "mobile-menu", type: "interaction"})
  }

  toggleSubmenu = ev => {
    ev.preventDefault()
    ev.stopPropagation()
    const anchor = ev.currentTarget

    const
      submenuEl = this.headerMenu.querySelector(`.header__submenu[data-submenu=${anchor.dataset.submenu}]`),
      isOpen = submenuEl.classList.contains("header__submenu--open")

    trackEventSP("click_nav_item", "browse", {label: anchor.textContent.trim(), type: "interaction"})

    // if target menu was already open, close the menu & return
    if (isOpen) {
      this.closeAllSubmenus()
      return
    }

    this.openSubmenu(submenuEl)
  }

  // handle "back" button on submenus in mobile view
  closeSubmenu = ev => {
    ev.preventDefault()
    ev.stopPropagation()

    this.openSubmenu(this.mobileNav)
    trackEventSP("click_nav_item", "browse", {label: "submenu-back", type: "interaction"})
  }

  closeMenu = ev => {
    if (ev.target === this.menuBackdrop) {
      ev.preventDefault()
      this.closeAllSubmenus()
    }
  }

  // plain links that do not have other associated JS behavior get this event handler for analytics
  trackOtherLinkClick = ev => {
    trackEventSP("click_nav_item", "browse", {label: ev.currentTarget.textContent.trim(), type: "link"})
  }

  setupHandlers = () => {
    if (this.transparentHeader) {
      this.onScroll()  // page could be loading already scrolled, so set correct state immediately
      document.addEventListener("scroll", throttle(this.onScroll, 100, {leading: true, trailing: true}))
    }

    this.mobileMenuToggle.addEventListener("click", this.toggleMobileMenu)

    for (const a of this.root.querySelectorAll("a[data-action=submenu]")) {
      a.addEventListener("click", this.toggleSubmenu)
    }

    for (const a of this.root.querySelectorAll("a[data-action=submenu-back]")) {
      a.addEventListener("click", this.closeSubmenu)
    }

    for (const a of this.root.querySelectorAll("a:not([data-action]):not([data-primary-cta])")) {
      a.addEventListener("click", this.trackOtherLinkClick)
    }

    this.menuBackdrop.addEventListener("click", this.closeMenu)

    window.addEventListener("pageshow", ev => { if (ev.persisted) { this.closeAllSubmenus() } })
  }
}
